import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  message,
} from 'antd';
import Table, { ColumnsType } from 'antd/es/table';
import {
  sysUserRegister,
  getRoleList,
  getAdminRoles,
  updateAdminRoles,
  getUmsAdminListV2,
  updateUmsAdmin,
  updateUmsAdminPassword,
  unlockUmsAdmin,
} from 'apis/sys';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import { findLabelByValue } from 'commons/options';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { SysRole } from 'types/sys';
import formatTime from 'utils/formatTime';
import _ from 'lodash';
import { AdminLoginLogDialog } from 'components/admin-login-log-dialog';
import AdminPermissionsDrawer from './admin-permissions-drawer';
import {
  parseAsInteger,
  parseAsString,
  parseAsBoolean,
  useQueryState,
} from 'nuqs';
import { SHOP_OPTION_LIST } from 'constant/shop';
import { useAppSelector } from 'store/hooks';
import { selectUserInfo } from 'store/slices/userInfoSlice';

type UmsAdmin = Awaited<ReturnType<typeof getUmsAdminListV2>>['list'][number];

const useQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [keyword, setKeyword] = useQueryState(
    'keyword',
    parseAsString.withDefault(''),
  );
  const [status, setStatus] = useQueryState('status', parseAsInteger);
  const [shop, setShop] = useQueryState('shop', parseAsInteger);
  const [isLocked, setIsLocked] = useQueryState('isLocked', parseAsBoolean);
  const [sortByLoginAttempts, setSortByLoginAttempts] = useQueryState(
    'sortByLoginAttempts',
    parseAsString,
  );

  return {
    pageNum,
    pageSize,
    keyword,
    status,
    shop,
    isLocked,
    sortByLoginAttempts,
    setPageNum,
    setPageSize,
    setKeyword,
    setStatus,
    setShop,
    setIsLocked,
    setSortByLoginAttempts,
  };
};

const AdminList = () => {
  const { isAdmin } = useAppSelector(selectUserInfo);
  const [addEditForm] = Form.useForm();
  const [adminRoleForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [editSysUser, setEditSysUser] = useState<UmsAdmin>();
  const [type, setType] = useState<'add' | 'edit'>('add');
  const [open, setOpen] = useState(false);
  const [openRole, setOpenRole] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);
  const [passwordAdmin, setPasswordAdmin] = useState<UmsAdmin>();
  const [roleData, setRoleData] = useState<SysRole[]>([]);
  const [openLoginLog, setOpenLoginLog] = useState(false);
  const [adminId, setAdminId] = useState<number>();
  const [openPermissions, setOpenPermissions] = useState(false);
  const [permissionsAdmin, setPermissionsAdmin] = useState<UmsAdmin>();
  const selectedShop = Form.useWatch('shop', addEditForm);

  const {
    pageNum,
    pageSize,
    keyword,
    status,
    shop,
    isLocked,
    sortByLoginAttempts,
    setPageNum,
    setPageSize,
    setKeyword,
    setStatus,
    setShop,
    setIsLocked,
    setSortByLoginAttempts,
  } = useQueryParams();

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<UmsAdmin>();

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);

      const { list, total } = await getUmsAdminListV2({
        pageNum,
        pageSize,
        keyword: keyword ?? undefined,
        status: status ?? undefined,
        shop: shop ?? undefined,
        isLocked: isLocked ?? undefined,
        sortByLoginAttempts:
          sortByLoginAttempts === 'ASC' || sortByLoginAttempts === 'DESC'
            ? sortByLoginAttempts
            : undefined,
      });

      setDataSource(list);
      setTotal(total);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [
    keyword,
    pageNum,
    pageSize,
    setDataSource,
    setLoading,
    setTotal,
    shop,
    status,
    isLocked,
    sortByLoginAttempts,
  ]);

  useEffect(() => {
    getRoleList().then((res) => {
      setRoleData(res);
    });
  }, []);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const onAdd = () => {
    setType('add');
    setEditSysUser(undefined);
    addEditForm.resetFields();
    setOpen(true);
  };

  const onEdit = useCallback(
    (sysUser: UmsAdmin) => {
      setType('edit');
      setOpen(true);
      setEditSysUser(sysUser);
      addEditForm.setFieldsValue({
        username: sysUser.username,
        nickName: sysUser.nickName,
        email: sysUser.email,
        shop: sysUser.shop,
        subShop: sysUser.subShop ?? [],
        note: sysUser.note,
      });
    },
    [addEditForm],
  );

  const onChangePassword = useCallback(
    (sysUser: UmsAdmin) => {
      setPasswordAdmin(sysUser);
      passwordForm.resetFields();
      setOpenPassword(true);
    },
    [passwordForm],
  );

  const onAddEdit = useCallback(async () => {
    const data = await addEditForm.validateFields();

    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        if (type === 'add') {
          await sysUserRegister({
            ...data,
          });
        }

        if (type === 'edit' && editSysUser?.id) {
          await updateUmsAdmin({
            id: editSysUser.id,
            nickName: data.nickName,
            email: data.email,
            note: data.note,
            shop: data.shop ?? null,
            subShop: data.subShop ?? [],
          });
        }

        message.success(i18n.t(LOCALS.successful_operation));
        setOpen(false);
        getDataSource();
      },
    });
  }, [addEditForm, editSysUser?.id, getDataSource, type]);

  const onPasswordFinish = useCallback(async () => {
    if (!passwordAdmin?.id) return;
    const data = await passwordForm.validateFields();

    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        await updateUmsAdminPassword({
          id: passwordAdmin.id,
          password: data.password,
        });
        message.success(i18n.t(LOCALS.successful_operation));
        setOpenPassword(false);
        setPasswordAdmin(undefined);
      },
    });
  }, [passwordAdmin?.id, passwordForm]);

  const getAdminRolesList = useCallback(
    async (id: number) => {
      if (roleData.length === 0) return;
      try {
        const list = await getAdminRoles(id);
        let roles: string[] = list.map((d) => d.roleId).map(String);
        // 排除禁用的
        const disabledRoles = roleData
          .filter((d) => !d.status)
          .map((d) => d.id)
          .map(String);
        const result = _.difference(roles, disabledRoles);
        adminRoleForm.setFieldsValue({
          adminId: id,
          roles: result,
        });
      } catch (err) {
      } finally {
      }
    },
    [adminRoleForm, roleData],
  );

  const onStatusChange = useCallback(
    async (checked: boolean, record: UmsAdmin) => {
      await updateUmsAdmin({
        id: record.id,
        status: checked ? 1 : 0,
      });
      message.success(i18n.t(LOCALS.successful_operation));
      getDataSource();
    },
    [getDataSource],
  );

  const onUnlock = useCallback(
    (sysUser: UmsAdmin) => {
      Modal.confirm({
        title: '确认解锁',
        content: `确定解锁账号 ${sysUser.username}？将重置登录尝试次数。`,
        onOk: async () => {
          await unlockUmsAdmin(sysUser.id);
          message.success(i18n.t(LOCALS.successful_operation));
          getDataSource();
        },
      });
    },
    [getDataSource],
  );

  const columns: ColumnsType<UmsAdmin> = useMemo(() => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: <Trans i18nKey={LOCALS.account} />,
        dataIndex: 'username',
        key: 'username',
      },
      {
        title: <Trans i18nKey={LOCALS.name} />,
        dataIndex: 'nickName',
        key: 'nickName',
      },
      {
        title: <Trans i18nKey={LOCALS.email} />,
        dataIndex: 'email',
        key: 'email',
        render: (email: UmsAdmin['email']) => {
          return <div className="max-w-32">{email || '-'}</div>;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.shop} />,
        dataIndex: 'shop',
        key: 'shop',
        render: (shop: UmsAdmin['shop']) => {
          return findLabelByValue(shop, SHOP_OPTION_LIST) || '-';
        },
      },
      {
        title: '子店铺',
        dataIndex: 'subShop',
        key: 'subShop',
        render: (subShop: UmsAdmin['subShop']) => {
          const subShopLabels = (subShop ?? [])
            .map((id) => findLabelByValue(id, SHOP_OPTION_LIST))
            .filter(Boolean);
          return subShopLabels.length > 0 ? subShopLabels.join('、') : '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.status} />,
        dataIndex: 'status',
        key: 'status',
        render: (status: UmsAdmin['status'], record: UmsAdmin) => {
          return (
            <Switch
              checked={!!status}
              onChange={(checked) => onStatusChange(checked, record)}
            />
          );
        },
      },
      {
        title: '锁定状态',
        dataIndex: 'isLocked',
        key: 'isLocked',
        render: (isLocked: UmsAdmin['isLocked']) => {
          return isLocked ? (
            <span style={{ color: '#ff4d4f' }}>已锁定</span>
          ) : (
            <span style={{ color: '#52c41a' }}>未锁定</span>
          );
        },
      },
      {
        title: '锁定到期时间',
        dataIndex: 'lockUntil',
        key: 'lockUntil',
        render: (lockUntil: UmsAdmin['lockUntil']) => {
          return lockUntil ? formatTime(lockUntil) : '-';
        },
      },
      {
        title: '登录尝试次数',
        dataIndex: 'loginAttempts',
        key: 'loginAttempts',
        render: (loginAttempts: UmsAdmin['loginAttempts']) => {
          return loginAttempts ?? 0;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_time} />,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createdTime: UmsAdmin['createTime']) => {
          return formatTime(createdTime);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.last_login_time} />,
        dataIndex: 'loginTime',
        key: 'loginTime',
        render: (loginTime: UmsAdmin['loginTime']) => {
          return formatTime(loginTime);
        },
      },
      {
        title: '最后登录信息',
        dataIndex: 'recentLoginLog',
        key: 'recentLoginLog',
        render: (recentLoginLog: UmsAdmin['recentLoginLog']) => {
          return (
            <div className="max-w-96">
              <div>
                <span className="mr-2 font-bold">地址：</span>
                {recentLoginLog?.address || '-'}
              </div>
              <div>
                <span className="mr-2 font-bold">IP：</span>
                {recentLoginLog?.ip || '-'}
              </div>
              <div>
                <span className="mr-2 font-bold">UA：</span>
                {recentLoginLog?.userAgent || '-'}
              </div>
            </div>
          );
        },
      },
      {
        key: 'options',
        title: <Trans i18nKey={LOCALS.options} />,
        render: (sysUser: UmsAdmin) => {
          return (
            <Space>
              <Button
                type="link"
                onClick={() => {
                  onEdit(sysUser);
                }}
              >
                <Trans i18nKey={LOCALS.edit}></Trans>
              </Button>
              <Button
                type="link"
                onClick={() => {
                  onChangePassword(sysUser);
                }}
              >
                修改密码
              </Button>
              <Button
                type="link"
                onClick={() => {
                  setOpenRole(true);
                  getAdminRolesList(sysUser.id);
                }}
              >
                <Trans i18nKey={LOCALS.assign_role}></Trans>
              </Button>
              <Button
                type="link"
                onClick={() => {
                  setPermissionsAdmin(sysUser);
                  setOpenPermissions(true);
                }}
              >
                <Trans i18nKey={LOCALS.permissions} />
              </Button>

              <Button
                type="link"
                onClick={() => {
                  setOpenLoginLog(true);
                  setAdminId(sysUser.id);
                }}
              >
                登录日志
              </Button>
              {isAdmin && sysUser.isLocked && (
                <Button type="link" danger onClick={() => onUnlock(sysUser)}>
                  解锁
                </Button>
              )}
            </Space>
          );
        },
      },
    ];
  }, [
    getAdminRolesList,
    isAdmin,
    onChangePassword,
    onEdit,
    onStatusChange,
    onUnlock,
  ]);

  const onRoleFinish = useCallback(() => {
    adminRoleForm.validateFields().then(async (val) => {
      await updateAdminRoles({
        ...val,
      });
      message.success(i18n.t('successful_operation'));
      setOpenRole(false);
      getDataSource();
    });
  }, [adminRoleForm, getDataSource]);

  return (
    <div>
      <Modal
        width="600px"
        open={open}
        title={<Trans i18nKey={type === 'add' ? LOCALS.add : LOCALS.edit} />}
        onCancel={() => {
          setOpen(false);
          addEditForm.resetFields();
        }}
        onOk={onAddEdit}
        destroyOnClose
      >
        <Form form={addEditForm} labelCol={{ span: 6 }}>
          <Form.Item
            label={<Trans i18nKey={LOCALS.account} />}
            name="username"
            rules={[
              {
                required: true,
                message: <Trans i18nKey={LOCALS.required_field} />,
              },
            ]}
          >
            <Input
              disabled={type === 'edit'}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>

          <Form.Item
            label={<Trans i18nKey={LOCALS.name} />}
            name="nickName"
            rules={[
              {
                required: true,
                message: <Trans i18nKey={LOCALS.required_field} />,
              },
            ]}
          >
            <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>

          {type === 'add' && (
            <Form.Item
              label={<Trans i18nKey={LOCALS.password} />}
              name="password"
              rules={[
                {
                  required: true,
                  message: <Trans i18nKey={LOCALS.required_field} />,
                },
              ]}
            >
              <Input.Password placeholder={i18n.t(LOCALS.please_enter) || ''} />
            </Form.Item>
          )}

          <Form.Item label={<Trans i18nKey={LOCALS.email} />} name="email">
            <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>

          <Form.Item name="shop" label={<Trans i18nKey={LOCALS.shop} />}>
            <Select
              allowClear
              options={SHOP_OPTION_LIST}
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
            />
          </Form.Item>

          {type === 'edit' && (
            <Form.Item name="subShop" label="子店铺">
              <Select
                mode="multiple"
                allowClear
                options={SHOP_OPTION_LIST.filter(
                  (option) => option.value !== selectedShop,
                )}
                placeholder={<Trans i18nKey={LOCALS.please_select} />}
              />
            </Form.Item>
          )}

          <Form.Item label={<Trans i18nKey={LOCALS.note} />} name="note">
            <Input.TextArea placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
        </Form>
      </Modal>

      <div className="space-y-4">
        <Card bodyStyle={{ padding: '16px' }}>
          <Form layout="inline">
            <Form.Item label={<Trans i18nKey={LOCALS.keyword} />}>
              <Input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={i18n.t(LOCALS.please_enter) || ''}
              />
            </Form.Item>

            <Form.Item
              label={<Trans i18nKey={LOCALS.status} />}
              className="w-60"
            >
              <Select
                value={status}
                onChange={(value) => setStatus(value)}
                options={[
                  { label: '启用', value: 1 },
                  { label: '禁用', value: 0 },
                ]}
                placeholder={<Trans i18nKey={LOCALS.please_select} />}
              />
            </Form.Item>

            <Form.Item label={<Trans i18nKey={LOCALS.shop} />} className="w-60">
              <Select
                value={shop}
                onChange={(value) => setShop(value)}
                options={SHOP_OPTION_LIST}
                placeholder={<Trans i18nKey={LOCALS.please_select} />}
              />
            </Form.Item>

            <Form.Item label="锁定状态" className="w-60">
              <Select
                value={
                  isLocked === null || isLocked === undefined
                    ? undefined
                    : isLocked
                }
                onChange={(value) =>
                  setIsLocked(value === undefined ? null : value)
                }
                allowClear
                options={[
                  { label: '已锁定', value: true },
                  { label: '未锁定', value: false },
                ]}
                placeholder={<Trans i18nKey={LOCALS.please_select} />}
              />
            </Form.Item>

            <Form.Item label="登录尝试次数排序" className="w-60">
              <Select
                value={sortByLoginAttempts}
                onChange={(value) => setSortByLoginAttempts(value)}
                allowClear
                options={[
                  { label: '升序', value: 'ASC' },
                  { label: '降序', value: 'DESC' },
                ]}
                placeholder={<Trans i18nKey={LOCALS.please_select} />}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  htmlType="button"
                  onClick={() => {
                    setKeyword('');
                    setStatus(null);
                    setShop(null);
                    setIsLocked(null);
                    setSortByLoginAttempts(null);
                    setPageNum(1);
                    setPageSize(10);
                  }}
                >
                  <Trans i18nKey={LOCALS.reset} />
                </Button>
                <Button onClick={onAdd}>
                  <Trans i18nKey={LOCALS.add} />
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card bodyStyle={{ padding: '16px' }}>
          <Table
            bordered
            pagination={{
              total,
              pageSize,
              current: pageNum,
              onChange: (page, pageSize) => {
                setPageNum(page);
                setPageSize(pageSize);
              },
            }}
            loading={loading}
            rowKey={'id'}
            dataSource={dataSource}
            columns={columns}
            scroll={{ x: 'max-content' }}
            tableLayout="auto"
          />
        </Card>
      </div>

      <Modal
        open={openRole}
        title={i18n.t(LOCALS.assign_role)}
        onCancel={() => setOpenRole(false)}
        onOk={onRoleFinish}
        destroyOnClose
        confirmLoading={loading}
      >
        <Form
          form={adminRoleForm}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
        >
          <Form.Item name="adminId" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            label={i18n.t('role_name')}
            name="roles"
            required
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select mode="multiple">
              {roleData.map((d) => (
                <Select.Option key={d.id} disabled={!d.status}>
                  {d.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={openPassword}
        title={`修改密码${passwordAdmin?.username ? ` - ${passwordAdmin.username}` : ''}`}
        onCancel={() => {
          setOpenPassword(false);
          setPasswordAdmin(undefined);
        }}
        onOk={onPasswordFinish}
        destroyOnClose
      >
        <Form form={passwordForm} labelCol={{ span: 6 }}>
          <Form.Item
            label={<Trans i18nKey={LOCALS.password} />}
            name="password"
            rules={[
              {
                required: true,
                message: <Trans i18nKey={LOCALS.required_field} />,
              },
              {
                min: 8,
                message: '密码至少 8 位',
              },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                message: '密码须包含大小写字母和数字',
              },
            ]}
          >
            <Input.Password placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
          <Form.Item
            label={<Trans i18nKey={LOCALS.repeat_password} />}
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: <Trans i18nKey={LOCALS.required_field} />,
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
        </Form>
      </Modal>

      <AdminLoginLogDialog
        open={openLoginLog}
        adminId={adminId}
        onClose={() => setOpenLoginLog(false)}
      />

      <AdminPermissionsDrawer
        open={openPermissions}
        adminId={permissionsAdmin?.id}
        adminName={permissionsAdmin?.username}
        onClose={() => {
          setOpenPermissions(false);
          setPermissionsAdmin(undefined);
        }}
      />
    </div>
  );
};

export default AdminList;
